import type { FCWithChildren } from '@pbk-web/types';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { DynamicDataChipInfo, DynamicDataDisplay, DynamicDataPickerProps } from '../../types';

/**
 * What dynamic-data editors need from the host: chip labels, the picker, and the added/removed reports.
 * - The host owns definitions and values; the editor only knows keys.
 */
export type LexicalDynamicData = {
  /** How a chip reads for `display`: a definition's friendly name (`builder`) or its resolved value (`viewer`). */
  chipFor: (key: string, display: DynamicDataDisplay) => DynamicDataChipInfo;
  /** The picker panel, rendered in a popover at the `/` (or the clicked chip); absent ⇒ no `/` menu. */
  renderPicker?: (props: DynamicDataPickerProps) => ReactNode;
  /** Chips show, but can't be inserted, edited or removed. */
  readOnly?: boolean;
  /** Whether clicking a chip opens it for editing; defaults to always. */
  canEdit?: (key: string) => boolean;
  /**
   * Fire when a key's first chip is added and its last one removed, by local edits only (undo included).
   * - Content that's loaded or comes from collaborators is not reported (see `MentionsFeature`).
   */
  onAdded?: (key: string) => void;
  onRemoved?: (key: string) => void;
};

const DynamicDataContext = createContext<LexicalDynamicData | null>(null);

/** Supplies dynamic data to every editor below it that enables `features.dynamicData`; nest one to override. */
export const LexicalDynamicDataProvider: FCWithChildren<{ value: LexicalDynamicData | null }> = ({ value, children }) => (
  <DynamicDataContext.Provider value={value}>{children}</DynamicDataContext.Provider>
);

/** `null` without a provider: chips show as unknown and `/` opens nothing. */
export const useLexicalDynamicData = (): LexicalDynamicData | null => useContext(DynamicDataContext);
